import { useNavigate } from 'react-router-dom'
import {
  principalHome,
  principalKey,
  principalLabel,
  usePrincipals,
  type Principal,
} from '@/hooks/useSession'
import { pt } from '@/i18n/pt-PT'

// Shown when a person holds more than one principal and none is active yet, and when they land on a
// route their ACTIVE principal can't enter but another one could. The alternative — bouncing them to
// the other area — deadlocks, because RequireRole and RequireClient redirect at each other.
export function PrincipalPicker({ principals }: { principals: Principal[] }) {
  const { active, switchTo } = usePrincipals()
  const navigate = useNavigate()

  // Without the navigate, choosing the other KIND of principal from a wrong-side route lands
  // straight back on this picker: the route's guard still can't admit the new principal.
  async function choose(p: Principal) {
    if (await switchTo(p)) navigate(principalHome(p))
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas p-8">
      <div className="w-full max-w-sm rounded-lg border border-DEFAULT bg-surface p-6 shadow-card">
        <h1 className="text-[15px] font-semibold text-gray-900">{pt.auth.choosePrincipal}</h1>
        <p className="mt-1 text-[12.5px] text-gray-600">{pt.auth.choosePrincipalHint}</p>
        <ul className="mt-4 space-y-2">
          {principals.map((p) => {
            const isActive = active !== null && principalKey(active) === principalKey(p)
            return (
              <li key={principalKey(p)}>
                <button
                  type="button"
                  onClick={() => void choose(p)}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left text-[13px] transition-colors ${
                    isActive
                      ? 'border-brand-800 bg-brand-50 text-brand-900'
                      : 'border-DEFAULT bg-surface text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{p.firmName}</span>
                    <span className="mt-0.5 block truncate text-[11.5px] text-gray-500">
                      {p.kind === 'staff'
                        ? pt.admin.consultants.roleOptions[p.role]
                        : pt.auth.principalClient}
                    </span>
                  </span>
                  {isActive ? (
                    <span className="ml-3 shrink-0 text-[11px] text-brand-800">
                      {pt.auth.principalActive}
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </main>
  )
}

export { principalLabel }
